import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { WORLDS, WORK, METRICS, NEWS } from "../data";
import { serif, mont, COLORS, es } from "./ui";

const Eyebrow = ({ children, color = COLORS.green }) => (
  <p className="text-xs uppercase tracking-[0.3em] mb-5" style={{ ...mont, color, fontWeight: 600 }}>{children}</p>
);

const V2Home = () => {
  return (
    <div data-testid="v2-home">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-20 grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Agencia + Estudio · Madrid</Eyebrow>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.98]" style={serif}>Marcamos la diferencia</h1>
            <p className="mt-8 text-lg leading-relaxed max-w-md" style={{ color: COLORS.sec }}>
              Marketing, comunicación y eventos. Producción audiovisual y contenidos de autor. Dos mundos, una misma visión.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/v2/proyectos" className="inline-flex items-center gap-2 px-6 py-3 text-xs font-bold uppercase tracking-[0.14em]" style={{ ...mont, backgroundColor: COLORS.ink, color: "#FBF9F5" }}>Ver proyectos <ArrowRight size={15} /></Link>
              <Link to="/v2/audiovisual" className="inline-flex items-center px-6 py-3 text-xs font-bold uppercase tracking-[0.14em]" style={{ ...mont, border: `1px solid ${COLORS.ink}`, color: COLORS.ink }}>El estudio</Link>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="overflow-hidden aspect-[4/5]" style={{ border: `1px solid ${COLORS.border}` }}>
              <img src={WORLDS[0].image} alt="Grabarte" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Dos mundos */}
      <section className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-2 gap-10" style={{ borderTop: `1px solid ${COLORS.border}` }}>
        {WORLDS.map((w, i) => (
          <Reveal key={w.id} delay={i * 0.1}>
            <Link to={i === 0 ? "/v2/audiovisual" : "/v2/marketing"} className="block bg-white shadow-sm group" style={{ border: `1px solid ${COLORS.border}` }}>
              <div className="overflow-hidden aspect-[16/10]">
                <img src={w.image} alt={w.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8">
                <Eyebrow>{w.label}</Eyebrow>
                <h3 className="text-3xl font-normal" style={serif}>{w.line}</h3>
                <p className="mt-3 text-sm leading-relaxed" style={{ color: COLORS.sec }}>{w.desc}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ ...mont, color: COLORS.green }}>Descubrir <ArrowRight size={14} /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>

      {/* Selected work */}
      <section className="max-w-6xl mx-auto px-6 py-20" style={{ borderTop: `1px solid ${COLORS.border}` }}>
        <div className="flex items-end justify-between mb-12">
          <h2 className="text-4xl font-normal" style={serif}>Proyectos seleccionados</h2>
          <Link to="/v2/proyectos" className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-2" style={{ ...mont, color: COLORS.green }}>Todos <ArrowRight size={13} /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {WORK.slice(0, 3).map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <div className="group">
                <div className="overflow-hidden aspect-[4/3] mb-4" style={{ border: `1px solid ${COLORS.border}` }}>
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="text-[10px] uppercase tracking-[0.22em]" style={{ ...mont, color: COLORS.gold, fontWeight: 600 }}>{p.kicker}</p>
                <h3 className="mt-1.5 text-xl font-medium" style={serif}>{p.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Metrics */}
      <section className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-6" style={{ borderTop: `1px solid ${COLORS.border}`, borderBottom: `1px solid ${COLORS.border}` }}>
        {METRICS.map((m) => (
          <div key={m.label}>
            <p className="text-5xl font-light" style={{ ...serif, color: COLORS.green }}>{m.value}<span style={{ color: COLORS.gold }}>{m.suffix}</span></p>
            <p className="mt-2 text-xs leading-snug" style={{ color: COLORS.sec }}>{m.label}</p>
          </div>
        ))}
      </section>

      {/* News preview */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-8">
          <h2 className="text-4xl font-normal" style={serif}>Actualidad</h2>
          <Link to="/v2/actualidad" className="text-[11px] uppercase tracking-[0.18em] inline-flex items-center gap-2" style={{ ...mont, color: COLORS.green }}>Ver más <ArrowRight size={13} /></Link>
        </div>
        {NEWS.map((n) => (
          <div key={n.title} className="py-6 flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-8" style={{ borderTop: `1px solid ${COLORS.border}` }}>
            <span className="text-[11px] uppercase tracking-[0.2em] shrink-0 w-40" style={{ ...mont, color: COLORS.green, fontWeight: 600 }}>{es(n.meta)}</span>
            <span className="text-xl sm:text-2xl font-normal" style={serif}>{es(n.title)}</span>
          </div>
        ))}
      </section>
    </div>
  );
};

export default V2Home;
